import type {SeedQuestionStatus} from '@cp/model';
import {useCallback, useEffect, useState} from 'react';
import countSeedQuestionsByFiltersDoc from '../../api/seedQuestion/countSeedQuestionsByFiltersDoc';

export interface CountSeedQuestionsVariables {
  moduleId: string;
  topics?: string[];
  statuses?: SeedQuestionStatus[];
}

export function useCountSeedQuestions() {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | undefined>(undefined);
  const [count, setCount] = useState<number>(0);

  const countSeedQuestions = useCallback(async (variables: CountSeedQuestionsVariables) => {
    setLoading(true);
    setError(undefined);
    try {
      const n = await countSeedQuestionsByFiltersDoc({
        moduleId: variables.moduleId,
        topics: variables.topics,
        statuses: variables.statuses,
      });
      setCount(n);
      return n;
    } catch (err) {
      const e = err instanceof Error ? err : new Error(String(err));
      setError(e);
      throw e;
    } finally {
      setLoading(false);
    }
  }, []);

  return {
    countSeedQuestions,
    loading,
    error,
    count,
  };
}

/**
 * Live total count for pagination when filters change (Firestore aggregation).
 */
export function useCountSeedQuestionsFetch(
  moduleId: string | undefined,
  topics?: string[],
  statuses?: SeedQuestionStatus[],
): number {
  const [total, setTotal] = useState(0);

  useEffect(() => {
    if (!moduleId) {
      setTotal(0);
      return;
    }

    let cancelled = false;
    countSeedQuestionsByFiltersDoc({moduleId, topics, statuses})
      .then((n) => {
        if (!cancelled) {
          setTotal(n);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setTotal(0);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [moduleId, topics, statuses]);

  return total;
}
